/**
 * Venta Empleados 2026 — employee-sale access controller.
 *
 * ---------------------------------------------------------------------------
 * SECURITY BOUNDARY NOTICE — READ BEFORE CHANGING THIS FILE
 * ---------------------------------------------------------------------------
 * Everything here runs in the visitor's browser. The session cookie it writes
 * can be forged by hand in about five seconds. This module is therefore a
 * USER-EXPERIENCE gate, not an authorization boundary.
 *
 * Real authorization for employee pricing, employee products and employee
 * discounts MUST be enforced by the backend/Shopify side that owns them.
 * See "Backend security requirements" in the Venta Empleados 2026 audit.
 *
 * What this module DOES guarantee:
 *   1. Authentication succeeds only on an explicitly valid API response.
 *      Timeout, offline, 4xx, 5xx, malformed JSON, wrong schema and unexpected
 *      exceptions all fail closed.
 *   2. A login attempt already in flight cannot be duplicated.
 *   3. A stale response can never overwrite a newer decision.
 *   4. Sessions carry an explicit expiry that is enforced on every page load.
 *   5. No credential is ever written to the console, the DOM, or a log.
 *
 * The pre-paint visibility decision lives in snippets/venta-empleados-boot.liquid,
 * not here — by the time this file executes the page has already painted, so it
 * is far too late to decide what may be shown.
 */
(function () {
  'use strict';

  /* Idempotent: the Theme Editor re-injects sections, and theme.liquid has
     historically loaded this asset more than once per page. */
  if (window.__ventaEmpleadosAuth) return;

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  var DEFAULT_TIMEOUT_MS = 10000;
  var MIN_TIMEOUT_MS = 1000;
  var MAX_TIMEOUT_MS = 30000;

  var defaults = {
    /* Overridden from Liquid so the endpoint is not duplicated across files. */
    endpoint: 'https://apis-ggv-staging-464139366683.northamerica-south1.run.app/ventaEmpleados',
    /* Employee sessions end at local midnight, matching the original design. */
    sessionEndsAtMidnight: true,
    /* Re-check an existing session against the API at most this often, so an
       employee disabled upstream loses access without waiting for expiry. */
    revalidateAfterMs: 15 * 60 * 1000,
  };

  /**
   * Resolve the request timeout to milliseconds.
   *
   * Theme settings arrive as SECONDS: Shopify rejects a `range` setting whose
   * max reaches 10000, so a millisecond range is not expressible in a schema.
   * `requestTimeoutMs` is still honoured as a programmatic override.
   *
   * A malformed theme setting must never yield NaN, 0, a negative or undefined:
   * a 0ms AbortController deadline would abort every request the instant it is
   * created and lock every employee out of the sale. Anything unusable falls
   * back to the default, and the result is clamped to a sane band.
   */
  /* Type-strict on purpose: Number(true) is 1 and Number('') is 0, so a loose
     coercion would silently accept a boolean as "1 second" or an empty setting
     as "0 seconds". Only a real number, or a string that is entirely numeric,
     counts. */
  function toPositiveNumber(value) {
    if (typeof value === 'number') return isFinite(value) && value > 0 ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
      var parsed = Number(value);
      return isFinite(parsed) && parsed > 0 ? parsed : null;
    }
    return null;
  }

  function resolveTimeoutMs(source) {
    var raw = source || {};

    var seconds = toPositiveNumber(raw.requestTimeoutSeconds);
    var ms = seconds !== null ? seconds * 1000 : toPositiveNumber(raw.requestTimeoutMs);

    if (ms === null) return DEFAULT_TIMEOUT_MS;
    return Math.min(Math.max(ms, MIN_TIMEOUT_MS), MAX_TIMEOUT_MS);
  }

  var config = Object.assign({}, defaults, window.VentaEmpleadosConfig || {});
  config.requestTimeoutMs = resolveTimeoutMs(window.VentaEmpleadosConfig);

  /* An endpoint blanked or broken in the Theme Editor must not produce a
     request to "undefined" — fall back to the shipped default. */
  if (typeof config.endpoint !== 'string' || !/^https:\/\//.test(config.endpoint)) {
    config.endpoint = defaults.endpoint;
  }

  var COOKIE = {
    flag: 'ventaEmpleados',
    employee: 'noEmp',
    expiresAt: 've_exp',
    lastCheck: 've_chk',
  };

  /* Company value in the <select> -> "libro" expected by the API. */
  var COMPANY_BOOKS = {
    GVMV: 'MASVISION',
    OLSA: 'OLSA',
    POSA: 'POSA',
    AOL: 'AOL',
    ICOFSA: 'ICOFSA',
    TIDETI: 'TIDE',
    Invitado: 'INVITADO',
  };

  var STATE = {
    BOOTING: 'booting',
    UNAUTHENTICATED: 'unauthenticated',
    AUTHENTICATING: 'authenticating',
    AUTHENTICATED: 'authenticated',
    ERROR: 'error',
    LOGGING_OUT: 'logging-out',
  };

  /* Deliberately generic. Distinguishing "no such employee" from "wrong
     company" would let anyone enumerate the employee directory. */
  var MESSAGES = {
    invalid: 'No pudimos validar los datos proporcionados. Verifica tu empresa y número de empleado.',
    incomplete: 'Selecciona tu empresa e ingresa tu número de empleado.',
    offline: 'Parece que no hay conexión. Revisa tu red e intenta de nuevo.',
    timeout: 'El servicio tardó demasiado en responder. Intenta de nuevo.',
    busy: 'Demasiados intentos. Espera un momento antes de volver a intentar.',
    unavailable: 'El servicio no está disponible en este momento. Intenta más tarde.',
    expired: 'Tu sesión terminó. Ingresa de nuevo para continuar.',
  };

  // ---------------------------------------------------------------------------
  // Cookie helpers
  // ---------------------------------------------------------------------------

  var isSecureContext = window.location.protocol === 'https:';

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[2]) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value, expiresAt) {
    /* Secure is set on https only, otherwise the cookie would be silently
       dropped on local/preview http origins. SameSite=Lax keeps the session out
       of cross-site requests without breaking normal top-level navigation.
       HttpOnly is impossible here by definition — see the boundary notice. */
    var parts = [
      name + '=' + encodeURIComponent(value),
      'path=/',
      'expires=' + new Date(expiresAt).toUTCString(),
      'SameSite=Lax',
    ];
    if (isSecureContext) parts.push('Secure');
    document.cookie = parts.join('; ');
  }

  function deleteCookie(name) {
    var parts = [name + '=', 'path=/', 'expires=Thu, 01 Jan 1970 00:00:00 GMT', 'SameSite=Lax'];
    if (isSecureContext) parts.push('Secure');
    document.cookie = parts.join('; ');
  }

  function sessionEndTimestamp() {
    var end = new Date();
    if (config.sessionEndsAtMidnight) {
      end.setHours(23, 59, 59, 999);
      /* Guard against a clock already past the boundary producing a past date. */
      if (end.getTime() <= Date.now()) end.setTime(Date.now() + 60 * 60 * 1000);
    } else {
      end.setTime(Date.now() + 12 * 60 * 60 * 1000);
    }
    return end.getTime();
  }

  // ---------------------------------------------------------------------------
  // Session
  // ---------------------------------------------------------------------------

  var session = {
    isValid: function () {
      if (readCookie(COOKIE.flag) !== 'true') return false;
      var expiresAt = parseInt(readCookie(COOKIE.expiresAt), 10);
      /* A legacy cookie with no parsable expiry is treated as expired.
         Unknown expiry is not a session. */
      if (!isFinite(expiresAt) || expiresAt <= 0) return false;
      return Date.now() < expiresAt;
    },

    employeeRef: function () {
      return readCookie(COOKIE.employee) || '';
    },

    start: function (employeeRef) {
      var expiresAt = sessionEndTimestamp();
      writeCookie(COOKIE.flag, 'true', expiresAt);
      writeCookie(COOKIE.employee, employeeRef, expiresAt);
      writeCookie(COOKIE.expiresAt, String(expiresAt), expiresAt);
      writeCookie(COOKIE.lastCheck, String(Date.now()), expiresAt);
    },

    clear: function () {
      deleteCookie(COOKIE.flag);
      deleteCookie(COOKIE.employee);
      deleteCookie(COOKIE.expiresAt);
      deleteCookie(COOKIE.lastCheck);
    },

    markChecked: function () {
      var expiresAt = parseInt(readCookie(COOKIE.expiresAt), 10);
      if (isFinite(expiresAt) && expiresAt > Date.now()) {
        writeCookie(COOKIE.lastCheck, String(Date.now()), expiresAt);
      }
    },

    isDueForRecheck: function () {
      var last = parseInt(readCookie(COOKIE.lastCheck), 10);
      if (!isFinite(last)) return true;
      return Date.now() - last > config.revalidateAfterMs;
    },
  };

  // ---------------------------------------------------------------------------
  // Cart lifecycle
  //
  // The employee cart must not outlive the employee session. In Shopify the two
  // are independent: the cart lives in its own cookie, server side, and knows
  // nothing about ve_exp. So when the session ends — the 12 hour expiry passes,
  // the cookie is deleted, or the employee presses Salir — the cart is left
  // holding employee-priced items that whoever uses that browser next can still
  // take to checkout.
  //
  // While a session is valid its cart is tagged with that session's expiry, and
  // a cart still carrying a tag once the session is gone is emptied. The tag is
  // what makes this safe: an ordinary customer's cart is never tagged, so this
  // code can never empty one. The value is a timestamp and nothing else — no
  // employee number goes into the cart, which would carry it through to the
  // order.
  //
  // Verified against the live store: POST /cart/clear.js empties the line items
  // but KEEPS the cart attributes, so the tag is removed in a second call.
  // ---------------------------------------------------------------------------

  var CART_TAG = '_ve_sesion';
  /* A logout must not be held hostage to the network. Whichever happens first —
     the cart request finishing or this deadline — the employee still leaves. */
  var LOGOUT_CART_DEADLINE_MS = 2500;

  function cartPost(path, body) {
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      /* Lets the request finish after the page starts unloading, which is what
         makes clearing on logout reliable — logout navigates straight away. */
      keepalive: true,
    });
  }

  /** The cart tag as rendered into the page by venta-empleados-boot.liquid. */
  function pageCartTag() {
    var value = window.VentaEmpleadosConfig && window.VentaEmpleadosConfig.cartSessionTag;
    return typeof value === 'string' && value !== '' ? value : null;
  }

  function emptyTaggedCart() {
    return cartPost('/cart/clear.js', {}).then(function () {
      /* clear.js leaves the attributes behind, so an emptied cart would still
         look like an employee cart on the next load. */
      var attributes = {};
      attributes[CART_TAG] = '';
      return cartPost('/cart/update.js', { attributes: attributes });
    });
  }

  /**
   * Tie the current cart to this session so it can be recognised later.
   *
   * Runs even when the cart is empty, on purpose: tagging it up front means
   * anything added afterwards is already inside a tagged cart. Waiting for a
   * non-empty cart would leave items added late in the session untagged, and a
   * session that expired before the next page load would not clean them up.
   *
   * At most one request per session: once the tag is rendered into the page,
   * every later load sees it and does nothing.
   */
  function tagCartForSession() {
    if (!session.isValid()) return Promise.resolve();
    var expiresAt = readCookie(COOKIE.expiresAt);
    if (!expiresAt || pageCartTag() === expiresAt) return Promise.resolve();
    var attributes = {};
    attributes[CART_TAG] = expiresAt;
    return cartPost('/cart/update.js', { attributes: attributes }).catch(function () {
      /* Tagging is best effort. Failing it must not block the sale — the worst
         case is a cart that is cleared on logout but not on silent expiry. */
    });
  }

  /**
   * Empty a cart left behind by a session that no longer exists.
   * @param {object} [options]
   * @param {boolean} options.sessionEndedHere true when this tab watched the
   *   session end. The cart is then known to be the employee's, so it is
   *   emptied without consulting the tag — which also covers a cart whose
   *   tagging never landed.
   */
  function clearCartIfSessionGone(options) {
    if (session.isValid()) return Promise.resolve();
    var known = !!(options && options.sessionEndedHere);
    if (!known && !pageCartTag()) return Promise.resolve();
    return emptyTaggedCart().catch(function () {
      /* Network failure leaves the tag in place, so the next load tries again
         rather than treating the cart as cleaned. */
    });
  }

  // ---------------------------------------------------------------------------
  // API client
  // ---------------------------------------------------------------------------

  /* Outcomes are an explicit closed set. Anything the client cannot positively
     classify as VALID is a denial — callers never see a truthy fallback. */
  var OUTCOME = {
    VALID: 'valid',
    INVALID: 'invalid',
    TIMEOUT: 'timeout',
    OFFLINE: 'offline',
    RATE_LIMITED: 'rate-limited',
    UNAVAILABLE: 'unavailable',
  };

  function messageForOutcome(outcome) {
    switch (outcome) {
      case OUTCOME.TIMEOUT:
        return MESSAGES.timeout;
      case OUTCOME.OFFLINE:
        return MESSAGES.offline;
      case OUTCOME.RATE_LIMITED:
        return MESSAGES.busy;
      case OUTCOME.UNAVAILABLE:
        return MESSAGES.unavailable;
      default:
        return MESSAGES.invalid;
    }
  }

  /* A denial that is definitive (the service answered, and the answer was "no")
     versus one that is transient (we never got an answer). Only definitive
     denials tear down an existing session during revalidation. */
  function isDefinitive(outcome) {
    return outcome === OUTCOME.VALID || outcome === OUTCOME.INVALID;
  }

  /**
   * The API answers with a JSON array of matching employees; a non-empty array
   * means the employee exists. Validate that shape explicitly — an object, null,
   * a string or an error envelope must never be mistaken for a match.
   */
  function isAuthoritativeMatch(payload) {
    if (!Array.isArray(payload)) return false;
    if (payload.length === 0) return false;
    var first = payload[0];
    return !!first && typeof first === 'object' && !Array.isArray(first);
  }

  function verifyEmployee(company, employeeNumber) {
    var book = COMPANY_BOOKS[company];
    if (!book) return Promise.resolve({ outcome: OUTCOME.INVALID });

    var url =
      config.endpoint +
      '?libro=' +
      encodeURIComponent(book) +
      '&filter[empleado]=' +
      encodeURIComponent(employeeNumber);

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timedOut = false;
    var timer = window.setTimeout(function () {
      timedOut = true;
      if (controller) controller.abort();
    }, config.requestTimeoutMs);

    var options = { method: 'GET', credentials: 'omit', redirect: 'follow' };
    if (controller) options.signal = controller.signal;

    return fetch(url, options)
      .then(function (response) {
        /* fetch() resolves on 4xx/5xx — status must be checked explicitly or an
           HTML error page would be parsed as if it were an auth decision. */
        if (response.status === 429) return { outcome: OUTCOME.RATE_LIMITED };
        if (response.status === 401 || response.status === 403) return { outcome: OUTCOME.INVALID };
        if (response.status >= 500) return { outcome: OUTCOME.UNAVAILABLE };
        if (!response.ok) return { outcome: OUTCOME.INVALID };

        return response
          .json()
          .then(function (payload) {
            return isAuthoritativeMatch(payload)
              ? { outcome: OUTCOME.VALID }
              : { outcome: OUTCOME.INVALID };
          })
          .catch(function () {
            /* Unparseable body: the service answered but we cannot read it.
               Treat as unavailable rather than as a denial we can act on. */
            return { outcome: OUTCOME.UNAVAILABLE };
          });
      })
      .catch(function () {
        if (timedOut) return { outcome: OUTCOME.TIMEOUT };
        if (navigator && navigator.onLine === false) return { outcome: OUTCOME.OFFLINE };
        return { outcome: OUTCOME.UNAVAILABLE };
      })
      .then(function (result) {
        window.clearTimeout(timer);
        return result;
      });
  }

  // ---------------------------------------------------------------------------
  // Controller
  // ---------------------------------------------------------------------------

  var root = document.documentElement;
  var state = STATE.BOOTING;

  /* Monotonic request id. A response whose generation is stale — because the
     employee logged out, or submitted again — is dropped instead of applied. */
  var generation = 0;

  var channel = null;
  try {
    if ('BroadcastChannel' in window) channel = new BroadcastChannel('venta-empleados-auth');
  } catch (e) {
    channel = null;
  }

  function setState(next) {
    state = next;
    /* 'authenticating' and 'logging-out' keep the previous visual gate: only
       settled states change what the pre-paint CSS reveals. */
    if (next === STATE.AUTHENTICATED) root.setAttribute('data-ve-auth', 'authenticated');
    else if (next === STATE.UNAUTHENTICATED || next === STATE.ERROR) {
      root.setAttribute('data-ve-auth', 'unauthenticated');
    }
    render();
  }

  // --- DOM -------------------------------------------------------------------

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function elements() {
    return {
      form: $('#ve-login-form'),
      company: $('#empresa'),
      employee: $('#noEmpleado'),
      submit: $('#ve-login-submit'),
      error: $('#ve-login-error'),
      gate: $('#sectionVE'),
    };
  }

  function showError(message) {
    var el = elements().error;
    if (!el) return;
    /* textContent, never innerHTML: messages are fixed strings today, but this
       node must stay safe if a future message ever carries API data. */
    el.textContent = message;
    el.hidden = false;
  }

  function clearError() {
    var el = elements().error;
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  // --- Modal focus containment ----------------------------------------------
  //
  // The gate is a real modal: position:fixed, full viewport, z-index 999999.
  // Without containment, Tab walks straight out of it into the storefront header
  // rendered behind the overlay — confirmed in browser QA (12 of 14 tab stops
  // landed outside). The markup carries role="dialog" + aria-modal="true"; this
  // supplies the behaviour those attributes promise.

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var gateActive = false;
  var focusBeforeGate = null;

  function focusablesIn(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function onGateKeydown(event) {
    if (event.key !== 'Tab') return;
    var gate = elements().gate;
    if (!gate || !gateActive) return;

    var items = focusablesIn(gate);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];

    /* Focus currently outside the gate (or on the way out) — pull it back. */
    if (!gate.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openGate() {
    var gate = elements().gate;
    if (!gate || gateActive) return;
    gateActive = true;
    focusBeforeGate = document.activeElement;
    document.addEventListener('keydown', onGateKeydown, true);
    /* Focus the first field, not the dialog itself, so a screen reader announces
       the labelled control the employee has to fill in. */
    var target = elements().company || focusablesIn(gate)[0];
    if (target) {
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }
  }

  function closeGate() {
    if (!gateActive) return;
    gateActive = false;
    document.removeEventListener('keydown', onGateKeydown, true);
    if (focusBeforeGate && document.contains(focusBeforeGate)) {
      try { focusBeforeGate.focus({ preventScroll: true }); } catch (e) {}
    }
    focusBeforeGate = null;
  }

  function render() {
    var el = elements();
    var busy = state === STATE.AUTHENTICATING || state === STATE.LOGGING_OUT;

    if (el.submit) {
      el.submit.disabled = busy;
      el.submit.setAttribute('aria-busy', busy ? 'true' : 'false');
      el.submit.textContent = busy ? 'Validando…' : 'Ingresar';
    }
    if (el.form) el.form.setAttribute('aria-busy', busy ? 'true' : 'false');

    if (el.gate) {
      /* The gate is an overlay: only an unauthenticated visitor on an employee
         page should ever see it. The pre-paint CSS enforces the same rule, this
         just keeps the attribute in sync for the transition. */
      var shouldGate = state === STATE.UNAUTHENTICATED || state === STATE.ERROR || busy;
      el.gate.setAttribute('lm-active', shouldGate ? 'true' : 'false');

      /* Only trap focus while the gate is genuinely the visible modal — the
         pre-paint CSS hides it entirely on non-employee pages, and trapping
         focus in an invisible element would strand keyboard users.

         Visibility is read from the computed style, NOT from offsetParent:
         offsetParent is always null for a position:fixed element, so an
         offsetParent check silently disables the trap on the very element it
         is meant to guard. Browser QA caught exactly that. */
      var onGatePage = root.getAttribute('data-ve-page') === 've';
      var gateShown = window.getComputedStyle(el.gate).display !== 'none';
      if (shouldGate && onGatePage && gateShown) openGate();
      else closeGate();
    }
  }

  // --- Actions ---------------------------------------------------------------

  function authenticate(company, employeeNumber) {
    if (state === STATE.AUTHENTICATING) return; // double-submit guard

    var cleanCompany = String(company || '').trim();
    /* Employee numbers are digits only. Strip anything else rather than sending
       it — a pasted "Emp. 1234" or a stray space would otherwise be encoded into
       the query and produce a confusing "not valid" for a correct number. */
    var cleanEmployee = String(employeeNumber || '').replace(/\D/g, '');

    if (!cleanCompany || !COMPANY_BOOKS[cleanCompany] || !cleanEmployee) {
      showError(MESSAGES.incomplete);
      var employeeField = elements().employee;
      if (employeeField && !cleanEmployee) employeeField.focus();
      return;
    }
    if (cleanEmployee.length > 64) {
      showError(MESSAGES.invalid);
      return;
    }

    clearError();
    setState(STATE.AUTHENTICATING);

    var requestId = ++generation;

    verifyEmployee(cleanCompany, cleanEmployee).then(function (result) {
      if (requestId !== generation) return; // superseded — drop it

      if (result.outcome === OUTCOME.VALID) {
        session.start(cleanCompany + cleanEmployee);
        setState(STATE.AUTHENTICATED);
        broadcast('authenticated');
        /* Reload so every Liquid-rendered surface re-evaluates with the session
           present. The pre-paint gate makes this reload flash-free. */
        window.location.reload();
        return;
      }

      /* Every non-VALID outcome leaves the visitor unauthenticated. */
      setState(STATE.UNAUTHENTICATED);
      showError(messageForOutcome(result.outcome));
      var field = elements().employee;
      if (field) field.focus();
    });
  }

  function logout(options) {
    var silent = options && options.silent;
    generation++; // invalidate any in-flight authentication
    setState(STATE.LOGGING_OUT);
    session.clear();
    root.setAttribute('data-ve-auth', 'unauthenticated');
    if (!silent) broadcast('logged-out');

    /* The cart goes with the session. session.clear() already ran, so this sees
       an ended session; sessionEndedHere says this tab watched it end, so the
       cart is emptied even if tagging never landed.

       Full reload afterwards: employee pricing and employee chrome are baked
       into the rendered page, so tearing them out in the DOM would be cosmetic.
       The deadline guarantees the employee leaves even if the request hangs —
       keepalive means it still completes after navigation starts. */
    var left = false;
    var leave = function () {
      if (left) return;
      left = true;
      window.location.assign('/');
    };
    clearCartIfSessionGone({ sessionEndedHere: true }).then(leave, leave);
    window.setTimeout(leave, LOGOUT_CART_DEADLINE_MS);
  }

  function broadcast(kind) {
    try {
      if (channel) channel.postMessage({ kind: kind, at: Date.now() });
      /* localStorage write doubles as the fallback signal for browsers without
         BroadcastChannel; it carries no session data of its own. */
      window.localStorage.setItem('ve-auth-sync', kind + ':' + Date.now());
    } catch (e) {
      /* Storage can be unavailable (private mode, blocked cookies). Multi-tab
         sync is a convenience; losing it must not break authentication. */
    }
  }

  function onRemoteAuthChange() {
    /* Another tab changed the session. Re-read the cookie — the cookie is the
       single source of truth, the message is only a nudge. */
    var valid = session.isValid();
    if (valid && state !== STATE.AUTHENTICATED) window.location.reload();
    if (!valid && state === STATE.AUTHENTICATED) window.location.reload();
  }

  // --- Revalidation ----------------------------------------------------------

  function revalidateExistingSession() {
    if (!session.isValid() || !session.isDueForRecheck()) return;

    var ref = session.employeeRef();
    var match = /^([A-Za-z]+)(.+)$/.exec(ref);
    if (!match) return;

    var company = Object.keys(COMPANY_BOOKS).filter(function (key) {
      return ref.indexOf(key) === 0;
    })[0];
    if (!company) return;

    var employeeNumber = ref.slice(company.length);
    if (!employeeNumber) return;

    var requestId = ++generation;

    verifyEmployee(company, employeeNumber).then(function (result) {
      if (requestId !== generation) return;

      if (result.outcome === OUTCOME.VALID) {
        session.markChecked();
        return;
      }
      /* Only a definitive "no" ends the session. A timeout or an outage must not
         sign out every employee — and would not stop an attacker, who can simply
         block the request. The hard expiry still bounds the session. */
      if (isDefinitive(result.outcome)) {
        session.clear();
        root.setAttribute('data-ve-auth', 'unauthenticated');
        setState(STATE.UNAUTHENTICATED);
        broadcast('logged-out');
        window.location.reload();
      }
    });
  }

  // --- Wiring ----------------------------------------------------------------

  /* Delegated listeners: they survive Theme Editor section reloads without ever
     being bound twice, which is why nothing here re-binds on shopify:section:load. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.id !== 've-login-form') return;
    event.preventDefault();
    var el = elements();
    authenticate(el.company && el.company.value, el.employee && el.employee.value);
  });

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-ve-logout]');
    if (!trigger) return;
    event.preventDefault();
    logout();
  });

  /* Employee number is digits only. Filtering on input (rather than relying on
     the pattern attribute alone) also covers paste, drag-drop and autofill, and
     keeps the caret where the employee expects it. Delegated, so it survives a
     Theme Editor section re-render. */
  document.addEventListener('input', function (event) {
    var field = event.target;
    if (!field || field.id !== 'noEmpleado') return;
    var digitsOnly = field.value.replace(/\D/g, '');
    if (digitsOnly === field.value) return;

    /* Preserve the caret: count the digits before it, then put it back after
       the same number of digits in the cleaned value. */
    var caret = typeof field.selectionStart === 'number' ? field.selectionStart : null;
    var digitsBeforeCaret = caret === null ? 0 : field.value.slice(0, caret).replace(/\D/g, '').length;
    field.value = digitsOnly;
    if (caret !== null) {
      try { field.setSelectionRange(digitsBeforeCaret, digitsBeforeCaret); } catch (e) {}
    }
  });

  window.addEventListener('storage', function (event) {
    if (event.key === 've-auth-sync') onRemoteAuthChange();
  });

  if (channel) {
    channel.onmessage = function () {
      onRemoteAuthChange();
    };
  }

  /**
   * Re-read the session and bring the page back in line with it.
   * The cookie is the single source of truth; this never calls the API, so it
   * is safe to run on every resume without introducing polling.
   */
  function resyncFromSession() {
    var valid = session.isValid();
    root.setAttribute('data-ve-auth', valid ? 'authenticated' : 'unauthenticated');
    /* Reload only when the page is now showing the wrong thing — employee
       chrome and employee pricing are baked into the rendered HTML, so fixing
       it in the DOM would be cosmetic. */
    if (valid !== (state === STATE.AUTHENTICATED)) {
      var acababaDeExpirar = !valid;
      setState(valid ? STATE.AUTHENTICATED : STATE.UNAUTHENTICATED);
      /* The session ended while this tab was away — expiry passed, the cookie
         was deleted, or another tab logged out. This tab watched the whole
         thing, so the cart is known to be the employee's and goes with it. The
         rendered tag is not consulted: it is a snapshot from before the items
         were added. The reload does not wait, because keepalive lets the
         request finish across it. */
      if (acababaDeExpirar) clearCartIfSessionGone({ sessionEndedHere: true });
      window.location.reload();
      return;
    }
    setState(valid ? STATE.AUTHENTICATED : STATE.UNAUTHENTICATED);
  }

  /* bfcache: a page restored with Back still has its old markup and none of the
     load-time handlers re-run. Without this, Back after logout hands the visitor
     a working snapshot of the employee UI. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    resyncFromSession();
  });

  /* Tab resumed after being backgrounded or suspended (common on mobile, where
     a tab can sit for hours). The session may have expired or another tab may
     have logged out while this one was frozen. Cookie read only — no request. */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    resyncFromSession();
    /* Only after the local state is settled do we consider a network re-check,
       and revalidateExistingSession() is itself throttled to once per
       config.revalidateAfterMs. */
    if (session.isValid()) revalidateExistingSession();
  });

  // --- "Comprar solo armazón" goes straight to the cart ----------------------
  //
  // The frame-only button is a plain submit inside Dawn's <product-form>, so an
  // add opened the cart notification and left the employee sitting on the
  // product page. On the employee sale that notification renders inside the
  // collapsed header section and paints as a narrow squashed column, and the
  // employee's next step is the cart anyway.
  //
  // Rather than racing the add with a redirect of our own, this selects the path
  // product-form.js already provides: when it finds neither a cart notification
  // nor a cart drawer, it navigates to routes.cart_url itself after a successful
  // add. Clearing `.cart` picks that branch, so the error handling, the sold-out
  // state and the GA4 add_to_cart push all keep working untouched. Every read of
  // `.cart` in that file is already null-guarded — this is a supported mode, not
  // a monkey-patch.
  //
  // The graduated-lens flow is unaffected: .btn_graduar / .btn_graduarVE are
  // anchors carrying type="submit", not buttons, so they never submit the form,
  // and the package flow adds through its own /cart/add links.
  //
  // This deliberately takes over EVERY <product-form> on an employee page, not
  // only the one holding "Comprar solo armazón". Two reasons. Landing on the
  // cart is the behaviour the employee sale wants from any add. And the boot CSS
  // may only hide the cart notification once nothing on the page still needs it
  // — which is what data-ve-cart-direct asserts. Arming selectively would leave
  // that claim false for whichever form was skipped.
  //
  // Clearing `.cart` is necessary but NOT sufficient on its own: the theme also
  // opens the notification by hand, from a jQuery handler bound straight to the
  // add-to-cart buttons in snippets/buy-buttons.liquid, which no amount of
  // product-form configuration can reach. The CSS rule keyed on
  // data-ve-cart-direct is the half of the fix that covers that path.

  function armDirectToCart() {
    /* Employee sale only. These forms are the ordinary storefront's too, and
       changing add-to-cart for regular customers is not this controller's job. */
    if (root.getAttribute('data-ve-page') !== 've') return 0;
    if (root.getAttribute('data-ve-auth') !== 'authenticated') return 0;

    var forms = document.querySelectorAll('product-form');
    var armed = 0;
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      if (form.veDirectToCart) { armed++; continue; }
      /* No `cart` property means product-form.js has not run its constructor on
         this element yet, and clearing it now would simply be overwritten. Leave
         it for a later pass — and leave the page unhidden until then. */
      if (!('cart' in form)) continue;
      form.cart = null;
      form.veDirectToCart = true;
      armed++;
    }

    /* Only claim it once every form on the page is actually on the redirect
       path. While one is still pending, the notification stays visible and the
       theme keeps working the way it always did. */
    if (armed === forms.length) root.setAttribute('data-ve-cart-direct', 'on');
    return armed;
  }

  /* Custom elements upgrade whenever product-form.js happens to load, which is
     not guaranteed to be before this script. Arm on definition, at init, on load
     and on Theme Editor section re-renders; the per-element flag makes every
     extra pass a no-op. */
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('product-form').then(armDirectToCart).catch(function () {});
  }
  window.addEventListener('load', armDirectToCart);
  document.addEventListener('shopify:section:load', armDirectToCart);

  /* Safety net for any load order the above misses: arm at click time, in the
     capture phase. That still runs before the form's own submit handler reads
     `.cart`, and before the theme's jQuery handler adds the `active` class —
     which matters, because setting data-ve-cart-direct here is what stops that
     class from painting. */
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;
      if (!event.target.closest('product-form')) return;
      armDirectToCart();
    },
    true
  );

  function init() {
    var valid = session.isValid();
    if (!valid && readCookie(COOKIE.flag) === 'true') {
      /* Flag without a valid expiry: a legacy or tampered session. Clear it so
         the page does not keep re-deciding on the same bad state. */
      session.clear();
    }
    setState(valid ? STATE.AUTHENTICATED : STATE.UNAUTHENTICATED);
    /* After setState, so it reads the settled data-ve-auth rather than the value
       the boot script stamped before the session was re-checked. */
    armDirectToCart();

    /* Cold load. A valid session claims its cart; an ended one takes its cart
       with it. The untagged case costs no request, which matters because this
       controller also loads on ordinary product pages. */
    if (valid) tagCartForSession();
    else clearCartIfSessionGone();

    if (valid) revalidateExistingSession();
  }

  window.__ventaEmpleadosAuth = {
    /* Intentionally narrow: state for debugging and logout for the UI. No
       function here can grant access — authenticate() is deliberately not
       exposed, and calling logout() from the console only ever removes access. */
    getState: function () {
      return state;
    },
    logout: logout,
    /* Read-only view of the resolved configuration, for diagnostics and tests.
       Both values are already public (the endpoint is in the page source), and
       a copy is returned so callers cannot mutate the live config. */
    getConfig: function () {
      return { endpoint: config.endpoint, requestTimeoutMs: config.requestTimeoutMs };
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
